import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'

const MEET_ADD_REQUEST_STATUS_CHIP = {
    1: { label: '신청', color: 'warning' },
    2: { label: '검토중', color: 'info' },
    3: { label: '등록완료', color: 'success' },
}

const USER_DELETE_STATUS_CHIP = {
    active: { label: '정상', color: 'default', variant: 'outlined' },
    deleted: { label: '삭제', color: 'error' },
}

const MEET_ADD_REQUEST_TABLE_COLUMNS = [
    { width: 70 },
    { width: 140 },
    { width: 180 },
    { width: 140 },
    { width: 260 },
    { width: 130 },
    { width: 120 },
    { width: 170 },
    { width: 120 },
]

const MEET_ADD_REQUEST_TABLE_MIN_WIDTH = MEET_ADD_REQUEST_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

export default function MeetAddRequests() {
    const location = useLocation()
    const history = useHistory()
    const deleteInFlightRef = useRef(new Set())
    const initialListStateRef = useRef(createInitialListState({ location }))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/meetAddRequest/getList', {
                page: curPage, offset: offset, searchText: searchQuery
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '대회 등록 문의 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '대회 등록 문의 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            if (showSpinner) {
                setLoading(false)
            }
        }
    }, [curPage, history, searchQuery])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading)

    const hasActiveFilters = Boolean((searchQuery || '').trim())

    // 조회 실패와 검색 결과 없음을 같은 테이블 영역에서 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="대회 등록 문의 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 대회 등록 문의가 없습니다."
                    description="이름/회사명/제목 검색어를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="접수된 대회 등록 문의가 없습니다."
                description="대회 등록 문의가 접수되면 이 목록에 표시됩니다."
            />
        )
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage !== 1) {
            setCurPage(1)
            if (searchQuery !== trimmedSearchText) {
                setSearchQuery(trimmedSearchText)
            }
            return
        }

        if (searchQuery !== trimmedSearchText) {
            setSearchQuery(trimmedSearchText)
            return
        }

        getData(false)
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id)) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data } = await API.post('/api/v1/admin/meetAddRequest/delete', { meetAddRequestId: id })
            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('대회 등록 문의를 삭제했습니다.')

                if (params.length === 1 && curPage > 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData()
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('대회 등록 문의 삭제 요청을 처리하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>대회 등록 문의 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="이름/회사명/제목"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                        </ListFilterForm>

                    </ListFilterBar>

                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: MEET_ADD_REQUEST_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {MEET_ADD_REQUEST_TABLE_COLUMNS.map((column, index) => (
                                <col key={index} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이름</TableCell>
                                <TableCell>회사명</TableCell>
                                <TableCell>지역</TableCell>
                                <TableCell>제목</TableCell>
                                <TableCell align="center">유저삭제여부</TableCell>
                                <TableCell align="center">상태</TableCell>
                                <TableCell align="center">등록일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.meetAddRequestId} hover>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>{row.user?.username || '-'}</TableCell>
                                        <TableCell>{row.companyName || '-'}</TableCell>
                                        <TableCell>{row.area || '-'}</TableCell>
                                        <TableCell className="listTableLimitText">{row.title || '-'}</TableCell>
                                        <TableCell align="center">
                                            <StatusChip status={row.delTime ? 'deleted' : 'active'} config={USER_DELETE_STATUS_CHIP} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <StatusChip status={row.status} config={MEET_ADD_REQUEST_STATUS_CHIP} fallbackLabel="상태 없음" />
                                        </TableCell>
                                        <TableCell align="center">{renderDateTime(row.regTime)}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup disabled={isBusy}>
                                                <EditIcon path={'meetAddRequest'} id={row.meetAddRequestId} search={location.search} curPage={curPage} searchText={searchQuery}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.meetAddRequestId}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
